<template>
  <NuxtLayout name="v1">
    <template #main class="relative">
      <div class="flex flex-col gap-2 py-2 min-h-screen" v-if = "!notAvailable">
        <div class="relative px-4" v-if="providerMeta">
          <V4StoreInfoComponent :data="providerMeta" class="py-1" :fandb="isFandB"/>
        </div>
        <UDivider v-if="isLocationAvailable" class="py-0.5"/>
        <div v-if="isLocationAvailable" class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div class="basis-full md:basis-1/4 md:order-last">
            <div class="w-full px-4 border-gray-300 py-1">
              <UInput padded v-model="searchText" :size="isMobile ? 'xl' : 'md'" placeholder="Start typing" icon="i-heroicons-magnifying-glass" :ui="{ color: { white: { outline: 'focus:ring-0.5 focus:ring-gray-400 focus:bg-transparent' } } }"/>
            </div>
          </div>
          <UDivider class="py-0.5 md:hidden"/>
          <div class="basis-full md:basis-3/4 pl-4 overflow-y-hidden scrollbar-hide">
            <V4CombinedFilterComponent :config="filterConfig" :preset="filters" :data="filterData" :dcount="4" @apply="applyFilters" />
          </div>
        </div>
        <UDivider v-if="isLocationAvailable" class="py-0.5 hidden md:block"/>

        <div v-if="!isLocationAvailable" class="flex justify-center pt-10 px-4">
                <div class="text-center max-w-md">
                    <div class="flex">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-red-600" viewBox="0 0 20 20"
                            fill="currentColor">
                            <path fill-rule="evenodd"
                                d="M10 2a6 6 0 00-6 6c0 4.418 6 10 6 10s6-5.582 6-10a6 6 0 00-6-6zm0 8a2 2 0 110-4 2 2 0 010 4z"
                                clip-rule="evenodd" />
                        </svg>
                        <p class="text-base sm:text-lg font-semibold flex items-start lg:items-center justify-center gap-2 flex-wrap sm:flex-nowrap mt-1">
                            Share your location to continue exploring this store
                        </p>
                    </div>
                    <p class="text-base sm:text-lg font-semibold mt-3">
                        Please
                        <button @click="openLocationSelector"
                            class="text-blue-600 underline hover:text-blue-800 focus:outline-none">
                            choose a location
                        </button>
                    </p>
                </div>
            </div>

        <!-- Products Display -->
        <div class="flex flex-row flex-wrap px-4 py-5" v-if="isLocationAvailable">
          <div v-for="(item, index) in productsInView" :key="index" class="w-full">
            <section :id="item.id" v-show="item.products.length > 0">
              <p 
              :data-category="`${item.name.toLowerCase()}`" 
              :data-products-count="item.products ? item.products.length : 0"
              class="text-md pb-5 text-gray-700 px-2" v-if="item.id !== 101">{{ item.name }} ({{ item.products.length }})</p>
              <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pb-5">
                <div class="w-full"
                  v-for="(product, pIndex) in item.products"
                  :key="pIndex"
                    :class="{ 'pointer-events-none grayscale opacity-50': !isEventEnabled || !product.is_stock_available }"
                >
                  <V4ProductCardComponent :product="product" @click="navigateToProduct(product)" :key="product.sku_id"/>
                </div>
              </div>
            </section>
          </div>
        </div>
        
        <!-- Loading Spinner -->
        <div class="text-gray-500 text-md text-center py-4" v-if="isFetching && isLocationAvailable">
          <UIcon name="i-ph-arrows-clockwise" class="h-5 w-5"/>
          <p>Loading products ...</p>
        </div>

        <div class="text-gray-500 text-md text-center py-4" v-if="productNotFound && !isFetching && isLocationAvailable">
         <p>No products found</p>
        </div>

        <div class="text-gray-500 text-md text-center py-4" v-if="isProviderDisabled && isLocationAvailable">
          <UIcon name="i-material-symbols-search-off" class="h-24 w-24 text-red-700"/>
          <p class="text-xl text-red-700">Store not available!</p>
        </div>
      </div>

      <!-- Menu Popover -->
      <div class="fixed z-40" :class="isMobile ? 'right-5 bottom-32' : 'left-1/2 bottom-20 transform -translate-x-1/2'" v-if="menuLength > 0 && !notAvailable">
        <UPopover :popper="{ arrow: true, placement: isMobile ? 'top-end' : 'top' }" v-model:open="isMenuOpened">
          <UButton block color="black" label="Menu" leading-icon="i-heroicons-book-open" size="xl" :class="isMobile ? 'flex flex-col px-3 py-2 text-sm' : ''"/>

          <template #panel>
            <div class="flex flex-col gap-4 max-h-96 overflow-scroll w-64 p-4 bg-stone-100">
              <div
                v-for="(item, index) in menu"
                :key="index"
                class="inline-flex items-start w-full justify-between gap-5 cursor-pointer"
                @click="handleMenuNavigation(item.id)"
              >
                <p class="line-clamp-2 dark:text-black">{{ item.name }}</p>
                <p>{{ item.products.length }}</p>
              </div>
            </div>
          </template>
        </UPopover>
      </div>
      <V3PageLoaderComponent v-if="isFetchingLocation" :text="loadingMessage" class="dark:text-black fixed top-0 left-0 w-full h-full flex items-center justify-center bg-white/80 z-50" />

    </template>

    <template #footer>
      <div class="">
          <div class="flex-col items-start" v-if="deliveryOffer && deliveryOffer.percent > 0">
            <div class=" flex">
              <div class="relative -mt-8 mx-4 md:ml-8 z-20">
              <div v-if="deliveryOffer.percent !== 100" class="relative w-[60px] h-[60px]">
                <div class="absolute inset-0 flex items-center justify-center z-0">
                  <V3ImageComponent
                    src="icons/mall4allabout.png"
                    class="w-5 h-5 object-contain"
                  />
                </div>
                <circle-progress
                  :percent="deliveryOffer.percent"
                  :size="60"
                  :border-width="8"
                  :border-bg-width="8"
                  fill-color="#067161"
                  empty-color="#D1D8D8"
                  background="none"
                  :linecap="'round'"
                  :transition="300"
                  :is-shadow="false"
                  :show-percent="false"
                  class="z-10"
                />
              </div>

              <V3ImageComponent
                v-else
                src="icons/confetti.png"
                class="rounded-full w-14 h-14 object-cover"
                style="background-color: #067161; padding: 10px;"
              />
            </div>

            <div class="flex items-center">
              <h5
                class="font-semibold text-sm md:text-md dark:text-gray-900"
                v-if="deliveryOffer.percent !== 100"
              >
              Add items worth &#8377;{{deliveryOffer?.remainingAmount}} more to get Free Delivery
              </h5>
              <h5
                class="font-semibold text-sm md:text-md dark:text-gray-900"
                v-else
              >
                Hooray! FREE DELIVERY unlocked.
              </h5>
            </div>
        </div>
        <UDivider class="py-4" />
      </div>
      </div>

      <div  v-if="itemCount > 0" class="flex justify-end w-full md:w-1/2 ml-auto p-2 bg-gray-100">
        <V3CartOverviewComponent />
      </div>

</template>
  </NuxtLayout>
</template>

<script setup>
import { V3ModalComponent, V3PopupComponent } from '#components';
import { useRoute, onBeforeRouteLeave } from 'vue-router';
import { onBeforeUnmount } from 'vue';
import {sizeMapping} from '../../../utility/sizeMapping';

const route = useRoute();
const providerId = route.params.id;
const locationId = ref(route.query.location_id);
const subCategory = ref(route.query.prefill_category);
const isMultipleLocations = ref(false);
const previousId = useState('previousId', () => '');
const isMenuOpened = ref(false);
const notAvailable = ref(true);
let locationsData = ref(null);
const { scrollToAnchor } = useAnchorScroll({
  toAnchor: { scrollOptions: { behavior: 'smooth', offsetTop: -96 } },
});
const { gtag } = useGtag();
const auth = useAuth();
const isAuthenticated = auth && auth.access_token;
const userData = isAuthenticated ? {
  user_name: auth.user.first_name,
  user_phone: auth.user.phone,
  user_email: auth.user.email,
} : { user_name: null, user_phone: null, user_email: null };

const isFocused = ref(false);
const { isMobile, isDesktop } = useDevice();
const cart = useCart();
import CircleProgress from "vue3-circle-progress";
const OfferStatus = computed(() => useCheckOffer())
const deliveryOffer = computed(() => OfferStatus.value.delivery?.[0] || {})
const itemCount = computed(() => cart.itemCount);

const search = useSearch();
search.update({ providers: [providerId] });
const location = useLocation();
const isGpsEnabled = location.gps.latitude && location.gps.longitude;
const isLocationAvailable = computed(() => location.isGPSEnabled);
const isFetchingLocation = ref(false);
const loadingMessage = ref('');
const productNotFound = ref(false);

const provider = reactive({});
const isProviderDisabled = computed(() => provider.value.status.toLowerCase() === 'disable');
const { isPopupOpen, openPopup, closePopup } = popupBackHandler();


const formatDistance = (distanceInKm) => {
  if (distanceInKm < 1) {
    const distanceInM = distanceInKm * 1000; // Convert to meters if less than 1 km
    return `${Math.round(distanceInM)} m`;
  }
  return `${Math.round(distanceInKm)} km`;
};

// if(!location.city?.name && isAuthenticated && route.path !== '/') {
//   navigateTo({ path: '/select-location', query: { from: 'home' } })
// }

const { data: providerItem } = await useAPIHandler('get', `/providers/${providerId}`, {
    query: { partner_id: useRuntimeConfig().public.partnerCode },
  });
if (providerItem) {
  provider.value = { ...providerItem };
  locationsData = providerItem?.locations;
  isMultipleLocations.value = providerItem?.locations?.length > 1 ? true :  false;
  notAvailable.value = false;
  if (!locationId.value) {
    if(isMultipleLocations)
    {
      if (isGpsEnabled && providerItem?.locations) {
          const userLat = location.gps.latitude;
          const userLng = location.gps.longitude;

          providerItem?.locations.forEach(location => {
            const [locationLng, locationLat] = location.gps.coordinates; // Assuming coordinates are in [longitude, latitude] order
            const distance = haversine(userLat, userLng, locationLat, locationLng);
            const actualDistance = distance / 1000;
            location.distance = formatDistance(actualDistance); // Format and add distance to the location object
          });

          // Sort locations based on the distance (ascending order)
          providerItem?.locations.sort((a, b) => {
            // Compare based on numeric distance, converting "km" and "m" to numeric values
            const distanceA = a.distance.includes('km') ? parseFloat(a.distance) * 1000 : parseFloat(a.distance);
            const distanceB = b.distance.includes('km') ? parseFloat(b.distance) * 1000 : parseFloat(b.distance);
            return distanceA - distanceB;
          });
        }
      locationId.value = providerItem?.locations?.[0]?.id;
    }
    else{
      locationId.value = providerItem?.locations?.[0]?.id;
    }    
  }
} else {
  notAvailable.value = true; 
}
// const provider = { ...providerItem, ...useTimingCheck(providerItem?.availability, providerItem?.locations) };
const isFandB = computed(() => provider.value?.category?.name.toUpperCase() === 'F&B');
const menu = ref([]);
const menuLength = computed(() => menu.value.length);
const foodType = ref(null);

const isServiceable = computed(() => providerMeta.value?.is_deliverable);

const isEventEnabled = computed(() => providerMeta.value?.is_open_now && isServiceable.value);
const isOpenNow = computed(() => providerMeta.value?.is_open_now);

const searchText = ref(null);
const isFetching = ref(false);
const totalProducts = ref(0);
// const products = ref([]);
// const page = ref(1);
const products = useState('products', () => []);
const page = useState('productPage', () => 1);

const pageSize = ref(isFandB.value ? 25000 : 100);
const isFetchingCompleted = ref(false);

const { getTrackingContext } = useTracking();
const trackingContext = getTrackingContext();

onActivated(() => {
});
onDeactivated(() => { });


const productsInView = computed(() => {
  const foodTypes = [foodType.value];
  if (foodType.value === 'non_veg') foodTypes.push('nonveg'); 
  const filtered = foodTypes.filter((k) => k).length
    ? products.value.filter((k) => k.food_type && foodTypes.includes(k.food_type.toLowerCase()))
    : products.value;

  if (menuLength.value === 0) {
    return [{ id: 101, name: 'All Products', products: filtered }];
  }
  const result = menu.value.map((item) => ({
    id: item.id,
    name: item.name,
    // products: filtered.filter((k) => item.products.includes(k.item_id.toString()) && k.is_stock_available),
    products: filtered.filter((k) => item.products.includes(k.item_id.toString()) && k.is_stock_available && typeof k.price === 'number'),
  }));
  const outOfStockProducts = filtered.filter((product) => !product.is_stock_available);

  if (outOfStockProducts.length > 0) {
    result.push({ id: 0, name: 'Currently Unavailable', products: outOfStockProducts });
  }
  return result;
});


function getMappedSizes(selectedSizes) {
  const expanded = [];
  selectedSizes.forEach(size => {
    const normalized = size.toLowerCase(); 
    if (sizeMapping[normalized]) {
      expanded.push(...sizeMapping[normalized]);
    } else {
      expanded.push(normalized);
    }
  });
  return expanded;
}

// new implementation
const providerMeta = ref(null);
const subCategories = providerItem?.available_categories?.map((k) => k.sub_category);
const attributes = useAttributeMapper({ subCategories: subCategories});

const filterConfig = {
  filter_sort_catalog: false,
  filter_sub_categories: true,
  filter_price_range: true,
  sort_better_discount: true,
  filter_food_type: isFandB.value,
  filter_is_stock_available: true,
  sort_price_ascending: true,
  sort_price_descending: true,
  filter_is_cancellable: isFandB.value ? false : true,
  filter_is_returnable: isFandB.value ? false : true
}
const filterData = computed(() => ({
  sub_categories: subCategories,
  attributes
}));

const queryParams = useFilterParser(route.query);
const filters = ref({
  location: { gps: [location.gps.latitude, location.gps.longitude] },
  providers: [providerId],
  ...(locationId.value ? { location_ids: [locationId.value] } : {}),
  include_catalog: true,
  catalog_filters: {
    price: [0, 100000],
    sort: providerId === 'DSF5DFADF03' ? 'price:asc' : 'discount:desc',
    ...queryParams.catalog,
    page: 1,
    page_size: pageSize.value
  },
  sort: 'name:asc',
  ...queryParams.provider,
  page: 1,
  page_size: 1
});
const applyFilters = async (providerItems = {}, catalogItems = {}) => {
  filters.value.catalog_filters = { ...filters.value.catalog_filters, ...catalogItems };
  filters.value = { ...filters.value, ...providerItems };
  page.value = 1;
  products.value = [];
  isFetchingCompleted.value = false;  
  await fetchProducts();
}

const fetchProducts = async () => {
  if (!isLocationAvailable.value || isProviderDisabled.value) return;


  if(filters.value.catalog_filters.page=== 1) {
    products.value = [];
  }

  isFetching.value = true;
  const { sanitized } = await useFilterSanitizer(filters.value);
  sanitized.catalog_filters.sort = [
    { field: 'is_stock_available', order: 'desc' },
    ...sanitized.catalog_filters.sort
  ];

  if (sanitized.catalog_filters.size) {
    sanitized.catalog_filters.size = getMappedSizes(sanitized.catalog_filters.size);
  }
  const body = {
    ...sanitized,
    page: page.value,
    page_size: pageSize.value,
    partner_id: useRuntimeConfig().public.partnerCode
  }

  
  //isFetching.value = true;
  const { data } = await useAPIHandler('post', '/v2/discover', { body });

  const provider = data?.providers?.[0];

  const catalog = provider?.catalog;
  const metadata = catalog?.metadata;
  const items = catalog?.products ?? [];

  totalProducts.value = provider?.total_products ?? 0;
  providerMeta.value = provider
    ? (({ catalog, ...rest }) => rest)(provider)
    : {};

  if (items.length) {
    products.value.push(...items);
  }

  if (metadata?.total != null && products.value.length >= metadata.total) {
    isFetchingCompleted.value = true;
  }

  isFetching.value = false;

  productNotFound.value = products.value.length === 0;
  
};
// end of new implementation

const setFoodType = (value) => {
  if (foodType.value === value) {
    foodType.value = null;
    return;
  }
  foodType.value = value;
};

const handleScroll = async () => {
  if (isFandB.value || isFetching.value || isFetchingCompleted.value || products.value.length >= totalProducts.value) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const documentHeight = document.documentElement.scrollHeight;

    if ((scrollTop + windowHeight) + 200 >= documentHeight) {
      filters.value.catalog_filters.page += 1;
      await fetchProducts();
    }
};

  onBeforeRouteLeave(() => window.removeEventListener('scroll', () => {}));

  const openLocationSelector = async () => {
    const routeUrl = useRoute();

    const popupOptions = {
        component: 'location',
        onSuccess: () => window.location.reload(),
        onClose: () => (isMobile ? slideover.close() : modal.close()),
    }

    if (!location.city?.name && !isAuthenticated) {
        isMobile
            ? slideover.open(V3PopupComponent, popupOptions)
            : modal.open(V3ModalComponent, popupOptions)
    } else if (!location.city?.name && isAuthenticated && routeUrl.path !== '/') {
        navigateTo({ path: '/select-location', query: { from: 'home' } })
    }
}
 
  const handleLocationError = (
      message = 'Something went wrong while fetching location. Please select manually.'
    ) => {
      loadingMessage.value = message
      isFetchingLocation.value = false

      const popupOptions = {
        component: 'location',
        onSuccess: () => window.location.reload(),
        onClose: () => (isMobile ? slideover.close() : modal.close()),
      }

      if (!location.city?.name && !isAuthenticated) {
        isMobile
            ? slideover.open(V3PopupComponent, popupOptions)
            : modal.open(V3ModalComponent, popupOptions)
      }else if(!location.city?.name && isAuthenticated && route.path !== '/'){
        navigateTo({ path: '/select-location', query: { from: 'home' } })
      }
  
    }
const openModal = (component, payload) => {
  // openPopup(); // Handles back button history push

  const options = {
    component,
    data: payload,
    onClose: () => {
      if (isMobile.value) {
        slideover.close();
      } else {
        modal.close();
      }
      history.replaceState(null, '', window.location.href); // Clear history entry
    },
  };

  if (isMobile.value) {
    slideover.open(V3PopupComponent, options);
  } else {
    modal.open(V3ModalComponent, options);
  }
};


onMounted(async () => {
  window.addEventListener('popstate', handleBackButton);
    // if (!isLocationAvailable.value) {
    //   loadingMessage.value = 'Fetching current location please wait...';
    //   isFetchingLocation.value = true;
    //   const { getCurrentLocation, emitter } = useLocationFinder();
    //   getCurrentLocation();
    //   emitter.on('success', async (coords) => {
    //       await location.setCoords(coords.latitude, coords.longitude);
    //       isFetchingLocation.value = false;
    //       loadingMessage.value = "";
    //       window.location.reload(); // reload to re-trigger any logic dependent on location
    //     });
    //     emitter.on('error', () => {
    //       handleLocationError('Unable to fetch location. Please select manually.')
    //     })
    //   return;
    // }
  localStorage.removeItem('selectedOrder');
  if (notAvailable.value) {
    const modalType = ref('');
    const { getTrackingContext } = useTracking()
    const trackingContext = getTrackingContext()
    const errorMessage = "Store unavailable. Please try again later or choose a different store."

    if (isMobile) {
      modalType.value = "v3-popup"
      slideover.open(V3PopupComponent, { component: 'error', data: {message: errorMessage},
      onClose: () => {
        slideover.close
        navigateTo({ path: "/" })
      }});
      gtag('event', 'error_popup_shown', {
      event_category: 'modal',
      page_type: route.fullPath,
      error_message: errorMessage,
      ...userData
    });
    } else {
      modalType.value = "v3-modal"
      modal.open(V3ModalComponent, { component: 'error', data: {message:errorMessage},
      onClose: () => {
        modal.close
        navigateTo({ path: "/" })
      }});
      gtag('event', 'error_popup_shown', {
      event_category: 'modal',
      page_type: trackingContext.current_page,
      error_message: errorMessage,
      ...userData
    });
    }

    const outsideModalClick = (event) => {
      const modalElement = document.querySelector(`[data-name="${modalType.value}"]`);
      if (modalElement && !modalElement.contains(event.target)) {
        modal.close();
        slideover.close();
        navigateTo({ path: '/' });
        document.removeEventListener('click', outsideModalClick);
      }
    };
    document.addEventListener('click', outsideModalClick);
  } else {
    const storeId = providerId;
    if (storeId !== previousId.value) {
      products.value = [];
      page.value = 1;
    }
    previousId.value = storeId;
  
    window.addEventListener('scroll', handleScroll);

  if (isFandB.value) {
    const { data: foodMenuData } = await useAPIHandler('post', '/customMenu', {
      body: { ukid: providerId },
    });
    menu.value = useMenuBuilder(foodMenuData);
  }
  
  
  await fetchProducts();
  
  if (!isOpenNow.value && isLocationAvailable.value ) {
    // if (isMobile) {
    //   slideover.open(V3PopupComponent, { component: 'storeClosed', data: provider.value, onClose: slideover.close });
    // } else {
    //   modal.open(V3ModalComponent, { component: 'storeClosed', data: provider.value, onClose: modal.close });
    // }
    openModal('storeClosed', provider.value);
  }
  else if (!isServiceable.value && isLocationAvailable.value) {
    // if (isMobile) {
    //   slideover.open(V3PopupComponent, { component: 'noservice', data: provider.value, onClose: slideover.close });
    // } else {
    //   modal.open(V3ModalComponent, { component: 'noservice', data: provider.value, onClose: modal.close });
    // }  
    openModal('noservice', provider.value);

  }

  // scroll to prefilled category
  usePrefilledCategory();  

}


// Watch isPopupOpen to close UI components when back button is pressed ---
// watch(isPopupOpen, (newVal) => {
//   if (!newVal) {
//     if (slideover.isOpen.value) slideover.close();
//     if (modal.isOpen.value) modal.close();
//   }
// });
// watchEffect(() => {
//   if (isPopupOpen.value && !modal.isOpen.value && !slideover.isOpen.value) {
//     closePopup(); // ensures we trigger history.back() even if closed by outside click
//   }
// });

const stickyElement = document.getElementById('stickyElement');
  if (stickyElement) {
    window.addEventListener('scroll', () => {
      const top = stickyElement.getBoundingClientRect().top;
      stickyElement.classList.toggle('py-3', top < 80);
      stickyElement.classList.toggle('border-b', top < 80);
    });
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('popstate', handleBackButton);
});



const navigateToProduct = (item) => {
  const action = isFocused.value && searchText.value && searchText.value.trim() ? 'search' : 'click';
  gtag('event', 'cta_event', {
    cta_name: 'product click',
    search_query: searchText.value && searchText.value.trim() ? searchText.value : null,
    action: action,
    referrer: 'Provider Screen',
    product_name: item?.name,
    product_id: item?.sku_id,
    category_name: item?.category?.name,
    parent_category_name: item?.category?.parent?.name,
    ...userData,
  });
  navigateTo({ path: `/products/${item.sku_id}` });
};


watch(isMenuOpened, (newValue) => {
  if (newValue && searchText.value?.length > 0) {
    searchText.value = '';
  }

  // Add this logic
  if (newValue) {
    // Menu is opening, push a new state
    history.pushState({ menuOpened: true }, '');
  } else {
    // Menu is closing, if the current state is the one we pushed, pop it
    if (history.state && history.state.menuOpened) {
      history.back(); // This effectively removes the state we pushed
    }
  }
});


const handleBackButton = (event) => {
  if (isMenuOpened.value) {
    isMenuOpened.value = false;
    // Don't push another state here, just stop propagation
    // Do NOT call history.pushState() here
  }
};

const handleMenuNavigation = (menuId) => {
  isMenuOpened.value = false;
  scrollToAnchor(menuId);
};

const debouncedSearch = useDebounce(async () => {
  filters.value.catalog_filters.search_text = searchText.value;
  page.value = 1;
  products.value = [];
  await fetchProducts();
  
}, 500);

watch(searchText, debouncedSearch);

const onFocus = () => {
  isFocused.value = true;
  gtag('event', 'provider_screen_search_event', { provider_name: provider.value?.name, ...userData });
};

const slideover = useSlideover();
const modal = useModal();

useSeoMeta({ title: `${provider.value?.name} - ${provider.value?.id}` });



gtag('event', 'plp_event', { 
  provider_name: provider.value?.name,
  provider_ukid: providerId,
  category_name: providerItem?.category?.name,
  sub_category_name: subCategory.value,
  source_page: trackingContext.source_page,
  current_page: trackingContext.current_page,
  ...userData 
});

definePageMeta({
  name: 'ProviderPage',
  // keepalive: true,
});

onBeforeRouteLeave((to, from, next) => {
  if (to.path === '/') {
    previousId.value = '';
  }
  next()
})
</script>
